import Cookies from 'js-cookie';

import type { CsrfUtils } from '@change-corgi/core/csrf';
import type { EnvironmentUtils } from '@change-corgi/core/environment';
import type { EventTracker } from '@change-corgi/core/eventTracker';
import { createEventTracker as _createEventTracker } from '@change-corgi/core/eventTracker';
import type { Facebook } from '@change-corgi/core/facebook';
import type { HttpClient } from '@change-corgi/core/http';
import type { Optimizely } from '@change-corgi/core/optimizely';
import type { UserAgentUtils } from '@change-corgi/core/userAgent';

import webappInfo from '#src/webappInfo';

import type { Session } from '#src/shared/session';

import type { AmplitudeGuidesAndSurveys } from '#src/app/shared/utils/amplitudeGuidesAndSurveys';
import type { AmplitudeReplay } from '#src/app/shared/utils/amplitudeReplay';

import { createBreakpointSizeDecorator } from './breakpointSizeDecorator';
import { createBrowserFeaturesDecorator } from './browserFeaturesDecorator';
import { createDarkModeDecorator } from './darkModeDecorator';
import { createFacebookDecorator } from './facebookDecorator';
import { createScreenWidthDecorator } from './screenWidthDecorator';
import { createScrollDepthDecorator } from './scrollDepthDecorator';

type Options = Readonly<{
	csrf: CsrfUtils;
	session: Session;
	environment: EnvironmentUtils;
	optimizely: Optimizely;
	userAgent: UserAgentUtils;
	http: HttpClient;
	facebook: Facebook;
	amplitudeGuidesAndSurveys: AmplitudeGuidesAndSurveys;
	amplitudeReplay: AmplitudeReplay;
}>;

declare global {
	// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
	interface Navigator {
		standalone: boolean | undefined;
	}
}

export function createEventTracker({
	csrf,
	environment,
	session,
	optimizely,
	userAgent,
	http,
	facebook,
	amplitudeGuidesAndSurveys,
	amplitudeReplay,
}: Options): EventTracker {
	const {
		country: { countryCode },
		locale: { localeCode: locale },
	} = session;

	const res = _createEventTracker({
		environment: environment.getEnvironment(),
		webappInfo,
		sessionInfo: { loginState: session.loginState, locale, countryCode },
		commonProperties: {
			...session.trackingData,
		},
		decorators: [
			optimizely,
			userAgent,
			amplitudeReplay,
			createFacebookDecorator({ session, facebook }),
			createBrowserFeaturesDecorator(locale),
			createDarkModeDecorator(),
			createBreakpointSizeDecorator(),
			createScreenWidthDecorator(),
			createScrollDepthDecorator(),
		],
		listeners: [optimizely, amplitudeGuidesAndSurveys],
		post: http.post.bind(http),
		sendBeacon: http.sendBeacon.bind(http),
		getCsrfToken: csrf.getCsrfToken.bind(csrf),
		cookies: Cookies,
		defaultLoggingEnabled: environment.getEnvironment() === 'development',
		// using sendBeacon when possible due to apparent issues with fetch.keepalive
		httpMode: 'beacon',
	}).decorateWindow();

	amplitudeGuidesAndSurveys.setEventTracker(res);

	return res;
}
